import { SyntheticEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Box, Container, Tabs, useTheme } from '@mui/material'

import { Account, BackofficePersona } from '@/domain/usecases'
import { FeatureFlagName, useFeatureFlags } from '@/infra/feature-flag'
import { i18nTranslationHelper } from '@/i18n'
import { makeSkillWebInstitutionsRedirectUrl } from '@/main/factories/http'
import PathConstants from '@/main/routes/paths'
import { ValidAccountState } from '@/presentation/contexts/auth'
import useAccount from '@/presentation/hooks/auth/use-account'
import { AnalyticsEventName, AnalyticsTracker } from '@/infra/tracking'
import { TabApp } from '@presentation/components/ui/navigation/tab/tab-app'
import { TabMenuApp } from '@presentation/components/ui/navigation/tab/tab-menu-app'
import {
  resolveAuthorizationsRoute,
  resolveCollaboratorsRoute,
  resolveCompaniesRoute
} from '../shared/nav-config'
import { useActiveTab } from '../shared/use-active-tab'

const { Routes } = PathConstants

// Desktop top bar (tabs). Counterpart for dropdown/mobile: nav-config.ts → buildManagerSection()
// When adding/removing items here, mirror the change in buildManagerSection.
export function DesktopNavMenuAdmin() {
  const theme = useTheme()
  const t = i18nTranslationHelper.getTComponents
  const [account] = useAccount() as ValidAccountState
  const params = useParams()

  const {
    useNewCollaboratorsFlow,
    useNewAuthorizationsFlow,
    useNewCompanyManager,
    isFitScoreApprovalEnabled,
    isScholarshipRenewalEnabled
  } = useFeatureFlags({
    useNewCollaboratorsFlow: FeatureFlagName.ENABLE_NEW_COLLABORATORS_FLOW,
    useNewAuthorizationsFlow: FeatureFlagName.ENABLE_NEW_AUTHORIZATIONS_FLOW,
    useNewCompanyManager: FeatureFlagName.ENABLED_NEW_COMPANY_MANAGER,
    isFitScoreApprovalEnabled: FeatureFlagName.ENABLE_FIT_SCORE_APPROVAL,
    isScholarshipRenewalEnabled: FeatureFlagName.ENABLE_BACKOFFICE_SCHOLARSHIP_RENEWAL
  })

  const collaboratorsRoute = resolveCollaboratorsRoute(useNewCollaboratorsFlow)
  const authorizationsRoute = resolveAuthorizationsRoute(useNewAuthorizationsFlow)
  const companiesRoute = resolveCompaniesRoute(useNewCompanyManager)

  // Personas de backoffice (membership): COMPANY_ADMIN = RH, INTERNAL = SuperAdmin
  const personas = Account.getPersonas(account)
  const isAdmin = personas.includes(BackofficePersona.COMPANY_ADMIN)
  const isBackoffice = personas.includes(BackofficePersona.INTERNAL)

  const tabSelectedValue = useActiveTab(pathname => {
    const routes: Array<PathConstants.RouteProps> = [
      ...(isBackoffice
        ? [
            Routes.INTERNAL_COMPANY_MANAGER_USER_BATCH_DISABLE,
            Routes.INTERNAL_COMPANY_MANAGER_INVOICE,
            Routes.INTERNAL_COMPANY_MANAGER_COMPANIES,
            Routes.INTERNAL_COMPANY_MANAGER_FIT_SCORE,
            Routes.COMPANY_MANAGER_COLLABORATORS,
            Routes.COMPANY_MANAGER_AUTHORIZATIONS,
            companiesRoute
          ]
        : []),
      ...(isAdmin
        ? [
            Routes.COMPANY_MANAGER_DASHBOARD,
            { ...Routes.COMPANY_MANAGER_EDIT_USER_PROFILE, name: collaboratorsRoute.name },
            {
              ...Routes.COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE,
              name: Routes.COMPANY_MANAGER_DEPENDENT_LIST.name
            },
            {
              ...Routes.COMPANY_MANAGER_HOLDER_SEARCH,
              name: Routes.COMPANY_MANAGER_DEPENDENT_LIST.name
            },
            {
              ...Routes.COMPANY_MANAGER_DEPENDENT_REGISTRATION,
              name: Routes.COMPANY_MANAGER_DEPENDENT_LIST.name
            },
            Routes.COMPANY_MANAGER_DEPENDENT_LIST,
            Routes.INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL,
            Routes.COMPANY_MANAGER_GROUPS
          ]
        : []),
      collaboratorsRoute,
      authorizationsRoute
    ]

    return routes.find(r => (r.computeValue?.(params) ?? r.value) === pathname)?.name ?? false
  })

  const handleTabClick = (_: SyntheticEvent, newValue: string | false) => {
    AnalyticsTracker.trackEvent(AnalyticsEventName.HEADER_NAV_MENU, { open: newValue })
  }

  const tabsSx = {
    minHeight: theme.spacing(8),
    height: theme.spacing(8)
  }

  const containerSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }

  if (!isAdmin && !isBackoffice) return null

  return (
    <Container maxWidth="lg" disableGutters>
      <Box sx={containerSx}>
        <Tabs
          value={tabSelectedValue}
          onChange={handleTabClick}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ height: theme.spacing('auto') }}
        >
          {/* Admin only: path '/backoffice/dashboard' | page: 'Dados' */}
          {isAdmin && (
            <TabApp
              component={Link}
              to={Routes.COMPANY_MANAGER_DASHBOARD.value}
              label={Routes.COMPANY_MANAGER_DASHBOARD.title}
              value={Routes.COMPANY_MANAGER_DASHBOARD.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Admin + Backoffice (shared, deduplicado): flag ENABLE_NEW_COLLABORATORS_FLOW */}
          <TabApp
            component={Link}
            to={collaboratorsRoute.value}
            label={collaboratorsRoute.title}
            value={collaboratorsRoute.name}
            selectedValue={tabSelectedValue}
            sx={tabsSx}
          />

          {/* Admin only: path '/backoffice/dependent-user' | page: 'Dependentes' */}
          {isAdmin && (
            <TabApp
              component={Link}
              to={Routes.COMPANY_MANAGER_DEPENDENT_LIST.value}
              label={Routes.COMPANY_MANAGER_DEPENDENT_LIST.title}
              value={Routes.COMPANY_MANAGER_DEPENDENT_LIST.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Admin + Backoffice (shared, deduplicado): flag ENABLE_NEW_AUTHORIZATIONS_FLOW */}
          <TabApp
            component={Link}
            to={authorizationsRoute.value}
            label={authorizationsRoute.title}
            value={authorizationsRoute.name}
            selectedValue={tabSelectedValue}
            sx={tabsSx}
          />

          {/* Admin only: path '/backoffice/groups' | page: 'Grupos' */}
          {/* TEMP: tab disabled while the Grupos page is broken. Route still exists,
              so direct navigation works. Restore `isAdmin &&` once the page is fixed. */}
          {false && (
            <TabApp
              component={Link}
              to={Routes.COMPANY_MANAGER_GROUPS.value}
              label={Routes.COMPANY_MANAGER_GROUPS.title}
              value={Routes.COMPANY_MANAGER_GROUPS.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* RH: tela nova de detalhe da própria empresa (loader decide via flag). */}
          {isAdmin && (
            <TabApp
              component={Link}
              to={
                account.company?.uniqueId
                  ? Routes.INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL.value.replace(
                      ':id',
                      account.company.uniqueId
                    )
                  : ''
              }
              label={t('navUserMenu.companyTitle')}
              value={Routes.INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Admin only: dropdown Solicitações */}
          {isAdmin && (
            <TabMenuApp
              label={t('navUserMenu.requestsTitle')}
              value="requests"
              selectedValue={tabSelectedValue}
              sx={tabsSx}
              options={[
                {
                  label: Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT.title,
                  to: Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT.value,
                  value: Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT.name
                },
                {
                  label: Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT.title,
                  to: Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT.value,
                  value: Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT.name
                },
                ...(isScholarshipRenewalEnabled
                  ? [
                      {
                        label: Routes.COMPANY_MANAGER_SCHOLARSHIP_RENEWAL.title,
                        to: Routes.COMPANY_MANAGER_SCHOLARSHIP_RENEWAL.value,
                        value: Routes.COMPANY_MANAGER_SCHOLARSHIP_RENEWAL.name
                      }
                    ]
                  : []),
                {
                  label: Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION.title,
                  to: Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION.value,
                  value: Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION.name
                }
              ]}
            />
          )}

          {/* Backoffice only: path '/backoffice/invoice' | page: 'Notas Fiscais' */}
          {isBackoffice && (
            <TabApp
              component={Link}
              to={Routes.INTERNAL_COMPANY_MANAGER_INVOICE.value}
              label={Routes.INTERNAL_COMPANY_MANAGER_INVOICE.title}
              value={Routes.INTERNAL_COMPANY_MANAGER_INVOICE.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Backoffice only: monolith '/app/institutions' | page: 'Parceiros' */}
          {isBackoffice && (
            <TabApp
              component={Link}
              to={makeSkillWebInstitutionsRedirectUrl()}
              label={t('navUserMenu.partnersTitle')}
              value="monolith_backoffice_partners"
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Backoffice only: flag ENABLED_NEW_COMPANY_MANAGER | page: 'Empresas' */}
          {isBackoffice && (
            <TabApp
              component={Link}
              to={companiesRoute.value}
              label={companiesRoute.title}
              value={companiesRoute.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Backoffice only: external catalog importer */}
          {isBackoffice && (
            <TabApp
              component="a"
              href="https://unicoskill.app.n8n.cloud/form/catalog-importer"
              target="_blank"
              rel="noopener noreferrer"
              label={t('navUserMenu.importCatalogTitle')}
              value="backoffice_import_catalog"
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          {/* Backoffice only: flag ENABLE_FIT_SCORE_APPROVAL | page: 'Fit Score' */}
          {isBackoffice && isFitScoreApprovalEnabled && (
            <TabApp
              component={Link}
              to={Routes.INTERNAL_COMPANY_MANAGER_FIT_SCORE.value}
              label={Routes.INTERNAL_COMPANY_MANAGER_FIT_SCORE.title}
              value={Routes.INTERNAL_COMPANY_MANAGER_FIT_SCORE.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}
        </Tabs>
      </Box>
    </Container>
  )
}
